import { rootApi } from '../rootApi';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  ParticpantAccount,
  CreateInterviewDto,
  UpdateInterviewDto,
  ReviewInterviewDto,
  ConfirmInterviewDto,
  ScheduledInterviewResponse,
} from './interview.types';

export const interviewsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getApplicationInterviews: builder.query<PaginatedResponse<ScheduledInterviewResponse>, { applicationId: string; params: PageOptions & { status?: "pending" | "completed" | "scheduled" } }>({
      query: ({ applicationId, params }) => ({
        url: `/jobs/applications/${applicationId}/interviews`,
        params
      }),
      providesTags: (result, error, { applicationId }) => [{ type: 'ApplicationInterviews', id: applicationId }],
    }),

    getPossibleInterviewParticipants: builder.query<ParticpantAccount[], { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/jobs/applications/${applicationId}/interviews/available-participants`,
      })
    }),

    scheduleInterview: builder.mutation<ScheduledInterviewResponse, { applicationId: string; data: CreateInterviewDto }>({
      query: ({ applicationId, data }) => ({
        url: `/jobs/applications/${applicationId}/interviews`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (result, error, { applicationId }) => [{ type: 'ApplicationInterviews', id: applicationId }],
    }),

    updateInterview: builder.mutation<APIResponse, { interviewId: string; data: UpdateInterviewDto }>({
      query: ({ interviewId, data }) => ({
        url: `/jobs/applications/interviews/${interviewId}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (result, error, { interviewId }) => [{ type: 'Interviews', id: interviewId }],
    }),

    cancelInterview: builder.mutation<APIResponse, { interviewId: string }>({
      query: ({ interviewId }) => ({
        url: `/jobs/applications/interviews/${interviewId}/cancel`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (result, error, { interviewId }) => [{ type: 'Interviews', id: interviewId }],
    }),

    getInterviewById: builder.query<ScheduledInterviewResponse, { interviewId: string }>({
      query: ({ interviewId }) => ({
        url: `/jobs/applications/interviews/${interviewId}`,
      }),
      providesTags: (result, error, { interviewId }) => [{ type: 'Interviews', id: interviewId }],
    }),

    reviewInterview: builder.mutation<APIResponse, { interviewId: string; data: ReviewInterviewDto }>({
      query: ({ interviewId, data }) => ({
        url: `/jobs/applications/interviews/${interviewId}/review`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (result, error, { interviewId }) => [{ type: 'Interviews', id: interviewId }],
    }),

    requestReschedule: builder.mutation<APIResponse, { interviewId: string; data: { notes: string } }>({
      query: ({ interviewId, data }) => ({
        url: `/jobs/applications/interviews/${interviewId}/request-reschedule`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (result, error, { interviewId }) => [{ type: 'Interviews', id: interviewId }],
    }),
    confirmInterview: builder.mutation<APIResponse, { interviewId: string; data: ConfirmInterviewDto }>({
      query: ({ interviewId, data }) => ({
        url: `/jobs/applications/interviews/${interviewId}/confirm`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (result, error, { interviewId }) => [{ type: 'Interviews', id: interviewId }],
    }),

    updateInterviewPhases: builder.mutation<APIResponse, { applicationId: string; phase: number }>({
      query: ({ applicationId, phase }) => ({
        url: `/jobs/applications/${applicationId}/interview-phase`,
        method: HTTPRequestType.PUT,
        data: {
          phase
        },
      }),
      invalidatesTags: (result, error, { applicationId }) => ['Applications'],
    }),
  }),
});

export const {
  useGetApplicationInterviewsQuery,
  useGetPossibleInterviewParticipantsQuery,
  useScheduleInterviewMutation,
  useGetInterviewByIdQuery,
  useCancelInterviewMutation,
  useUpdateInterviewMutation,
  useReviewInterviewMutation,
  useRequestRescheduleMutation,
  useConfirmInterviewMutation,
  useUpdateInterviewPhasesMutation,
} = interviewsApi;
